import React, { useEffect, useState } from 'react';
import useDriverService from '../../../services/useDriverService';
import {
  DriverPassHistoryEntry,
  DriverPassHistoryResponse,
} from '../../../models/DriverPassHistoryModel';
import '../../../components/Services/styles/ServiceHistoryTable.css';

type Props = {
  driverId: number;
  refreshKey?: number;
};

const DriverPassHistoryPanel: React.FC<Props> = ({ driverId, refreshKey = 0 }) => {
  const { getPassHistory } = useDriverService();
  const [history, setHistory] = useState<DriverPassHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    getPassHistory(driverId, {
      onSuccess: (response: any) => {
        const data: DriverPassHistoryResponse = response?.data ?? response;
        setHistory(data.history ?? []);
        setLoading(false);
      },
      onError: (err: any) => {
        setError(err?.message ?? 'Error al cargar el historial');
        setLoading(false);
      },
    });
  }, [driverId, refreshKey, getPassHistory]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('es-CL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return dateString;
    }
  };

  const getChangeTypeIcon = (changeType: string) => {
    switch (changeType) {
      case 'PASS_STATUS':
        return '🔘';
      case 'DATE_FIELD':
        return '📅';
      case 'VEHICLE_RELEASE':
        return '🚗';
      default:
        return '📝';
    }
  };

  const getChangeTypeColor = (changeType: string) => {
    switch (changeType) {
      case 'PASS_STATUS':
        return 'status-change';
      case 'DATE_FIELD':
        return 'field-update';
      case 'VEHICLE_RELEASE':
        return 'assignment-change';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="service-history-container history-embedded">
        <div className="history-loading">
          <div className="spinner" />
          <p>Cargando historial…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="service-history-container history-embedded">
        <div className="history-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="service-history-container history-embedded">
        <div className="history-empty">
          <p>No hay cambios registrados en el historial de este conductor.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="service-history-container history-embedded">
      <div className="history-header">
        <span className="history-count">{history.length} cambios registrados</span>
      </div>

      <div className="history-table-wrapper">
        <table className="history-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Tipo</th>
              <th>Usuario</th>
              <th>Pase</th>
              <th>Descripción</th>
              <th>Anterior</th>
              <th>Nuevo</th>
            </tr>
          </thead>
          <tbody>
            {history.map((item) => (
              <tr
                key={item.id}
                className={`history-row ${getChangeTypeColor(item.change_type)}`}
              >
                <td className="history-date">{formatDate(item.created_at)}</td>
                <td className="history-type">
                  <span className="type-badge">
                    {getChangeTypeIcon(item.change_type)} {item.change_type_label}
                  </span>
                </td>
                <td className="history-user">{item.user_full_name}</td>
                <td className="history-description">{item.pass_type_label}</td>
                <td className="history-description">{item.change_description}</td>
                <td className="history-old-value">
                  {item.old_value?.trim() ? item.old_value : '—'}
                </td>
                <td className="history-new-value">
                  {item.new_value?.trim() ? item.new_value : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DriverPassHistoryPanel;
